// ================================================================
// 使用者詳情頁面
// ================================================================
// 版本：1.0
// 日期：2026-01-26
// 功能：顯示單一使用者詳細資訊
// ================================================================

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  Grid,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  InputAdornment,
  IconButton,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { ArrowBack, Person, AccountBalanceWallet, QueryStats, Lock as LockIcon, Visibility, VisibilityOff, CalendarToday } from '@mui/icons-material';
import { getUserDetail, resetUserPassword, updateUserBirthTime, UserDetail, Transaction, Query } from '../../api/admin';
import AdminLayout from '../../components/admin/AdminLayout';

// 完整的用戶詳情資料結構
interface UserDetailData {
  user: UserDetail;
  transactions: Transaction[];
  queries: Query[];
  stats: { total_queries: number; total_cost: number };
}

// ================================================================
// 主要組件
// ================================================================

const UserDetailPage: React.FC = () => {
  const { userId } = useParams<{ userId: string }>();
  const navigate = useNavigate();

  const [userDetail, setUserDetail] = useState<UserDetailData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // 修改出生時間 Dialog 狀態
  const [birthTimeOpen, setBirthTimeOpen] = useState(false);
  const [btYear, setBtYear] = useState('');
  const [btMonth, setBtMonth] = useState('');
  const [btDay, setBtDay] = useState('');
  const [btHour, setBtHour] = useState('23_early');
  const [btGender, setBtGender] = useState('Female');
  const [btError, setBtError] = useState<string | null>(null);
  const [btSuccess, setBtSuccess] = useState<string | null>(null);
  const [isBtSaving, setIsBtSaving] = useState(false);

  // 重設密碼 Dialog 狀態
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [resetError, setResetError] = useState<string | null>(null);
  const [resetSuccess, setResetSuccess] = useState<string | null>(null);
  const [isResetting, setIsResetting] = useState(false);

  // 時辰選項（13 個，子時分凌晨/深夜）
  const HOUR_OPTIONS = [
    { value: '23_early', label: '子時（凌晨 00:01–01:00）' },
    { value: '01', label: '丑時（01:01–03:00）' },
    { value: '03', label: '寅時（03:01–05:00）' },
    { value: '05', label: '卯時（05:01–07:00）' },
    { value: '07', label: '辰時（07:01–09:00）' },
    { value: '09', label: '巳時（09:01–11:00）' },
    { value: '11', label: '午時（11:01–13:00）' },
    { value: '13', label: '未時（13:01–15:00）' },
    { value: '15', label: '申時（15:01–17:00）' },
    { value: '17', label: '酉時（17:01–19:00）' },
    { value: '19', label: '戌時（19:01–21:00）' },
    { value: '21', label: '亥時（21:01–23:00）' },
    { value: '23_late', label: '子時（深夜 23:01–24:00）' },
  ];

  // 將表單值轉換為 YYYYMMDDhh 格式（處理子時日期偏移）
  const buildBirthTime = (year: string, month: string, day: string, hour: string): string => {
    let y = parseInt(year, 10);
    let m = parseInt(month, 10);
    let d = parseInt(day, 10);
    let hh: string;
    if (hour === '23_early') {
      const dt = new Date(y, m - 1, d);
      dt.setDate(dt.getDate() - 1);
      y = dt.getFullYear(); m = dt.getMonth() + 1; d = dt.getDate();
      hh = '23';
    } else if (hour === '23_late') {
      hh = '23';
    } else {
      hh = hour;
    }
    return `${y}${String(m).padStart(2, '0')}${String(d).padStart(2, '0')}${hh}`;
  };

  // 開啟修改出生時間 Dialog，預填現有值
  const handleOpenBirthTimeDialog = () => {
    if (!userDetail) return;
    const bt = userDetail.user.birth_time || '';
    if (bt.length >= 10) {
      let y = parseInt(bt.substring(0, 4), 10);
      let m = parseInt(bt.substring(4, 6), 10);
      let d = parseInt(bt.substring(6, 8), 10);
      const hh = bt.substring(8, 10);
      if (hh === '23') {
        const dt = new Date(y, m - 1, d);
        dt.setDate(dt.getDate() + 1);
        y = dt.getFullYear(); m = dt.getMonth() + 1; d = dt.getDate();
      }
      setBtYear(String(y));
      setBtMonth(String(m));
      setBtDay(String(d));
      setBtHour(hh === '23' ? '23_early' : hh);
    } else {
      setBtYear(''); setBtMonth(''); setBtDay(''); setBtHour('23_early');
    }
    setBtGender(userDetail.user.gender || 'Female');
    setBtError(null); setBtSuccess(null);
    setBirthTimeOpen(true);
  };

  const handleCloseBirthTimeDialog = () => {
    setBirthTimeOpen(false);
    setBtError(null); setBtSuccess(null);
  };

  const handleSaveBirthTime = async () => {
    setBtError(null); setBtSuccess(null);
    const y = parseInt(btYear, 10);
    const m = parseInt(btMonth, 10);
    const d = parseInt(btDay, 10);
    if (!btYear || isNaN(y) || y < 1900 || y > 2100) {
      setBtError('請填寫正確的西元年（1900–2100）'); return;
    }
    if (!btMonth || isNaN(m) || m < 1 || m > 12) {
      setBtError('請填寫正確的月份（1–12）'); return;
    }
    if (!btDay || isNaN(d) || d < 1 || d > 31) {
      setBtError('請填寫正確的日期（1–31）'); return;
    }
    setIsBtSaving(true);
    try {
      const bt = buildBirthTime(btYear, btMonth, btDay, btHour);
      const response = await updateUserBirthTime(parseInt(userId!, 10), bt, btGender);
      if (response.success) {
        setBtSuccess(response.message);
        await loadUserDetail();
        setTimeout(() => { setBirthTimeOpen(false); setBtSuccess(null); }, 1500);
      } else {
        setBtError(response.message || '更新失敗');
      }
    } catch (err: any) {
      setBtError(err.message || '發生錯誤');
    } finally {
      setIsBtSaving(false);
    }
  };

  // 重設密碼 handler
  const handleResetPassword = async () => {
    setResetError(null);
    setResetSuccess(null);
    if (!newPassword || newPassword.length < 6) {
      setResetError('新密碼長度至少需要 6 個字元');
      return;
    }
    setIsResetting(true);
    try {
      const response = await resetUserPassword(parseInt(userId!, 10), newPassword);
      if (response.success) {
        setResetSuccess(response.message);
        setTimeout(() => {
          setResetPasswordOpen(false);
          setNewPassword('');
          setResetSuccess(null);
          setResetError(null);
        }, 1500);
      } else {
        setResetError(response.message || '重設密碼失敗');
      }
    } catch (err: any) {
      setResetError(err.message || '發生錯誤');
    } finally {
      setIsResetting(false);
    }
  };

  const handleCloseResetDialog = () => {
    setResetPasswordOpen(false);
    setNewPassword('');
    setResetError(null);
    setResetSuccess(null);
    setShowNewPassword(false);
  };

  // 載入使用者詳情
  const loadUserDetail = useCallback(async () => {
    if (!userId) return;

    setIsLoading(true);
    setError(null);
    try {
      const response = await getUserDetail(parseInt(userId, 10));
      if (response.success && response.user) {
        setUserDetail({
          user: response.user,
          transactions: response.transactions || [],
          queries: response.queries || [],
          stats: response.stats || { total_queries: 0, total_cost: 0 },
        });
      } else {
        setError(response.message || '無法載入使用者資料');
      }
    } catch (err: any) {
      setError(err.message || '載入失敗');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    loadUserDetail();
  }, [loadUserDetail]);

  // 格式化出生時間
  const formatBirthTime = (birthTime: string): string => {
    if (!birthTime || birthTime.length < 10) return birthTime || '-';
    let y = parseInt(birthTime.substring(0, 4), 10);
    let m = parseInt(birthTime.substring(4, 6), 10);
    let d = parseInt(birthTime.substring(6, 8), 10);
    const hour = birthTime.substring(8, 10);

    const hourMap: { [key: string]: string } = {
      '23': '子時', '01': '丑時', '03': '寅時', '05': '卯時',
      '07': '辰時', '09': '巳時', '11': '午時', '13': '未時',
      '15': '申時', '17': '酉時', '19': '戌時', '21': '亥時',
    };

    // 子時（hh=23）命盤計算日 = 儲存日 +1 天
    if (hour === '23') {
      const dt = new Date(y, m - 1, d);
      dt.setDate(dt.getDate() + 1);
      y = dt.getFullYear(); m = dt.getMonth() + 1; d = dt.getDate();
    }

    return `${y}年 ${m}月 ${d}日 ${hourMap[hour] || hour}時`;
  };

  if (isLoading) {
    return (
      <AdminLayout>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress />
        </Box>
      </AdminLayout>
    );
  }

  if (error || !userDetail) {
    return (
      <AdminLayout>
        <Box>
          <Button
            startIcon={<ArrowBack />}
            onClick={() => navigate('/admin/users')}
            sx={{ mb: 2 }}
          >
            返回使用者列表
          </Button>
          <Alert severity="error">{error || '使用者資料不存在'}</Alert>
        </Box>
      </AdminLayout>
    );
  }

  const { user, transactions, queries, stats } = userDetail;
  const isActive = !user.is_suspended || user.is_suspended === 0;

  return (
    <AdminLayout>
      <Box>
        {/* 頁面標題 */}
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              startIcon={<ArrowBack />}
              onClick={() => navigate('/admin/users')}
            >
              返回
            </Button>
            <Typography variant="h4" fontWeight="bold">
              使用者詳情
            </Typography>
          </Box>
          <Box display="flex" gap={1}>
            <Button
              variant="outlined"
              startIcon={<CalendarToday />}
              onClick={handleOpenBirthTimeDialog}
            >
              修改出生時間
            </Button>
            <Button
              variant="outlined"
              color="warning"
              startIcon={<LockIcon />}
              onClick={() => setResetPasswordOpen(true)}
            >
              重設密碼
            </Button>
          </Box>
        </Box>

        <Grid container spacing={3}>
          {/* 基本資訊卡片 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <Person color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  基本資訊
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Box display="flex" flexDirection="column" gap={1.5}>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">ID</Typography>
                  <Typography fontWeight="bold">{user.user_id}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">姓名</Typography>
                  <Typography fontWeight="bold">{user.full_name || '-'}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">Email</Typography>
                  <Typography>{user.email || '-'}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">電話</Typography>
                  <Typography>{user.phone_number || '-'}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">性別</Typography>
                  <Typography>{user.gender === 'Male' ? '男' : user.gender === 'Female' ? '女' : '-'}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">出生時間</Typography>
                  <Typography>{formatBirthTime(user.birth_time)}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">狀態</Typography>
                  <Chip
                    label={isActive ? '活躍' : '停用'}
                    color={isActive ? 'success' : 'error'}
                    size="small"
                  />
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">註冊時間</Typography>
                  <Typography>{new Date(user.created_at).toLocaleString('zh-TW')}</Typography>
                </Box>
                <Box display="flex" justifyContent="space-between">
                  <Typography color="text.secondary">最後登入</Typography>
                  <Typography>
                    {user.last_login_at ? new Date(user.last_login_at).toLocaleString('zh-TW') : '-'}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Grid>

          {/* 帳戶資訊卡片 */}
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AccountBalanceWallet color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  帳戶資訊
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        目前餘額
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="primary.main">
                        ${user.balance?.toFixed(2) || '0.00'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        累計消費
                      </Typography>
                      <Typography variant="h5" fontWeight="bold" color="secondary.main">
                        ${user.total_spent?.toFixed(2) || '0.00'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        查詢次數
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {stats.total_queries || 0}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography color="text.secondary" gutterBottom>
                        偏好貨幣
                      </Typography>
                      <Typography variant="h5" fontWeight="bold">
                        {user.preferred_currency || 'NTD'}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>
            </Paper>
          </Grid>

          {/* 最近交易記錄 */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <AccountBalanceWallet color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  最近交易記錄
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {transactions.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={3}>
                  暫無交易記錄
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>類型</TableCell>
                        <TableCell align="right">金額</TableCell>
                        <TableCell align="right">餘額</TableCell>
                        <TableCell>說明</TableCell>
                        <TableCell>時間</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {transactions.map((tx) => (
                        <TableRow key={tx.transaction_id}>
                          <TableCell>
                            <Chip
                              label={tx.type === 'credit' ? '充值' : tx.type === 'debit' ? '消費' : tx.type}
                              color={tx.type === 'credit' ? 'success' : 'default'}
                              size="small"
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Typography
                              color={tx.amount >= 0 ? 'success.main' : 'error.main'}
                              fontWeight="bold"
                            >
                              {tx.amount >= 0 ? '+' : ''}{tx.amount.toFixed(2)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">${tx.balance_after.toFixed(2)}</TableCell>
                          <TableCell>{tx.description || '-'}</TableCell>
                          <TableCell>{new Date(tx.created_at).toLocaleString('zh-TW')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>

          {/* 最近查詢記錄 */}
          <Grid item xs={12}>
            <Paper sx={{ p: 3 }}>
              <Box display="flex" alignItems="center" gap={1} mb={2}>
                <QueryStats color="primary" />
                <Typography variant="h6" fontWeight="bold">
                  最近查詢記錄
                </Typography>
              </Box>
              <Divider sx={{ mb: 2 }} />

              {queries.length === 0 ? (
                <Typography color="text.secondary" textAlign="center" py={3}>
                  暫無查詢記錄
                </Typography>
              ) : (
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>問題</TableCell>
                        <TableCell>類別</TableCell>
                        <TableCell align="right">費用</TableCell>
                        <TableCell>時間</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {queries.map((query) => (
                        <TableRow key={query.query_id}>
                          <TableCell>{query.question_text || query.question_id}</TableCell>
                          <TableCell>
                            <Chip label={query.category_id} size="small" />
                          </TableCell>
                          <TableCell align="right">${query.cost?.toFixed(2) || '0.00'}</TableCell>
                          <TableCell>{new Date(query.created_at).toLocaleString('zh-TW')}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </Paper>
          </Grid>
        </Grid>
      </Box>

      {/* 修改出生時間 Dialog */}
      <Dialog open={birthTimeOpen} onClose={handleCloseBirthTimeDialog} maxWidth="xs" fullWidth>
        <DialogTitle>修改出生時間</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
            {userDetail && (
              <Typography variant="body2" color="text.secondary">
                使用者：{userDetail.user.full_name}（{userDetail.user.email}）
              </Typography>
            )}
            {btError && <Alert severity="error">{btError}</Alert>}
            {btSuccess && <Alert severity="success">{btSuccess}</Alert>}
            <TextField
              label="西元年"
              type="number"
              value={btYear}
              onChange={(e) => setBtYear(e.target.value)}
              disabled={isBtSaving || !!btSuccess}
              inputProps={{ min: 1900, max: 2100 }}
              helperText="例：1985"
              fullWidth
            />
            <Box display="flex" gap={1}>
              <FormControl fullWidth>
                <InputLabel>月</InputLabel>
                <Select value={btMonth} label="月" onChange={(e) => setBtMonth(String(e.target.value))} disabled={isBtSaving || !!btSuccess}>
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((n) => (
                    <MenuItem key={n} value={String(n)}>{n} 月</MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>日</InputLabel>
                <Select value={btDay} label="日" onChange={(e) => setBtDay(String(e.target.value))} disabled={isBtSaving || !!btSuccess}>
                  {Array.from({ length: 31 }, (_, i) => i + 1).map((n) => (
                    <MenuItem key={n} value={String(n)}>{n} 日</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <FormControl fullWidth>
              <InputLabel>時辰</InputLabel>
              <Select value={btHour} label="時辰" onChange={(e) => setBtHour(e.target.value as string)} disabled={isBtSaving || !!btSuccess}>
                {HOUR_OPTIONS.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>性別</InputLabel>
              <Select value={btGender} label="性別" onChange={(e) => setBtGender(e.target.value as string)} disabled={isBtSaving || !!btSuccess}>
                <MenuItem value="Male">男命</MenuItem>
                <MenuItem value="Female">女命</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBirthTimeDialog} disabled={isBtSaving}>取消</Button>
          <Button onClick={handleSaveBirthTime} variant="contained" disabled={isBtSaving || !!btSuccess}>
            {isBtSaving ? '儲存中...' : '確認修改'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* 重設使用者密碼 Dialog */}
      <Dialog open={resetPasswordOpen} onClose={handleCloseResetDialog} maxWidth="xs" fullWidth>
        <DialogTitle>重設使用者密碼</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            {userDetail && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                使用者：{userDetail.user.full_name}（{userDetail.user.email}）
              </Typography>
            )}
            {resetError && <Alert severity="error" sx={{ mb: 2 }}>{resetError}</Alert>}
            {resetSuccess && <Alert severity="success" sx={{ mb: 2 }}>{resetSuccess}</Alert>}
            <TextField
              fullWidth
              type={showNewPassword ? 'text' : 'password'}
              label="新密碼"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              disabled={isResetting || !!resetSuccess}
              helperText="至少 6 個字元"
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowNewPassword(!showNewPassword)} edge="end">
                      {showNewPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseResetDialog} disabled={isResetting}>取消</Button>
          <Button
            onClick={handleResetPassword}
            variant="contained"
            color="warning"
            disabled={isResetting || !!resetSuccess}
          >
            {isResetting ? '重設中...' : '確認重設'}
          </Button>
        </DialogActions>
      </Dialog>
    </AdminLayout>
  );
};

export default UserDetailPage;
